import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type ReactNode } from 'react';
import { Controller, type Control } from 'react-hook-form';

import { type EventFormValues, type EventTextFieldName } from '@/domain/events/types';

interface Props extends Omit<TextFieldProps, 'name' | 'label' | 'error'> {
  name: EventTextFieldName;
  control: Control<EventFormValues>;
  label: string;
  optionalLabel?: string;
  hint?: string;
  /** Reformats the value when the field loses focus (CEP). */
  formatOnBlur?: (value: string) => string;
  onBlurValue?: (value: string) => void;
  children?: ReactNode;
}

/** Labelled, controlled text input with the field error underneath (same anatomy as the onboarding). */
const EventFormField = ({
  name,
  control,
  label,
  optionalLabel,
  hint,
  formatOnBlur,
  onBlurValue,
  children,
  ...textFieldProps
}: Props) => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState }) => (
      <FormControl fullWidth>
        <FormLabel htmlFor={name}>
          {label}
          {optionalLabel ? (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
              {optionalLabel}
            </Typography>
          ) : null}
        </FormLabel>
        <TextField
          {...textFieldProps}
          {...field}
          id={name}
          fullWidth
          error={Boolean(fieldState.error)}
          helperText={fieldState.error?.message ?? hint}
          onBlur={() => {
            const value = String(field.value ?? '');
            const formatted = formatOnBlur && value ? formatOnBlur(value) : value;
            if (formatted !== value) field.onChange(formatted);
            field.onBlur();
            onBlurValue?.(formatted);
          }}
        >
          {children}
        </TextField>
      </FormControl>
    )}
  />
);

export default EventFormField;
